import HighlightInput from "highlightable-input/react";
import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState
} from "react";

import { getAllEntities } from "@/api/entity";
import { getEntityProperties } from "@/api/property";
import { useInfiniteQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useInView } from "react-intersection-observer";
import { HashLoader } from "react-spinners";

import { PropertyType } from "@/types/form/property";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
  SelectItem
} from "@/components/ui/select";

import FormulaToolbar from "./formula-toolbar";

// اضافه کردن import چک‌باکس

interface Props extends React.ComponentProps<"div"> {
  onClose: () => void;
  onConfirm?: () => void;
  element: HTMLElement;
  formBody?: string;
}

interface JoinConfig {
  id: string;
  tableId: string;
  tableName: string; // real backend table name
  tableLabel: string; // UI display name
  joinKey: string;
  mainTableKey: string;
}

const ReceiveDatabaseModal: React.FC<Props> = ({
  element,
  onClose,
  onConfirm,
  formBody
}) => {
  const inputElement = element as HTMLInputElement;
  const { ref, inView } = useInView();
  const conditionFieldId = useId();
  const conditionElementRef = useRef<HTMLElement | null>(null);
  const conditionSelectionRef = useRef<number | null>(null);
  const pendingCursorRef = useRef<number | null>(null);
  const extractDataNamesWithIds = (
    htmlString: string
  ): { dataName: string; id: string }[] => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");

    // انتخاب عناصری که هم data-name دارند و هم id
    const elements = doc.querySelectorAll("[data-name][id]");

    return Array.from(elements).map((el) => ({
      dataName: el.getAttribute("data-name") || "",
      id: el.getAttribute("id") || ""
    }));
  };

  // در کامپوننت خود:
  const dataNames = extractDataNamesWithIds(formBody ?? "");
  const [searchValue, setSearchValue] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, 1500); // 500 میلی‌ثانیه تأخیر

    return () => clearTimeout(timer);
  }, [searchValue]);

  const { isFetchingNextPage, hasNextPage, isLoading, data, fetchNextPage } =
    useInfiniteQuery({
      queryKey: ["forms", debouncedSearch],
      queryFn: async ({ pageParam }) => {
        const response = await getAllEntities({
          page: pageParam,
          size: 1000,
          search: debouncedSearch
        });

        return response;
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        if (!lastPage?.data || lastPage.data.length === 0) {
          return undefined;
        }

        const totalCount = lastPage.totalCount;
        const listSize = lastPage.listSize;
        const lastPageNumber = lastPage.listNumber;

        const totalPages = Math.ceil(totalCount / listSize);
        if (lastPageNumber < totalPages) {
          return lastPageNumber + 1;
        }
        return undefined;
      }
    });
  // useEffect(() => {
  //   if (!isFetchingNextPage && hasNextPage) {
  //     fetchNextPage();
  //   }
  // }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  const [selectedEntityId, setSelectedEntityId] = useState<string>(
    inputElement.getAttribute("data-tableId") ?? ""
  );
  const [filter, setFilter] = useState(
    inputElement.getAttribute("data-filter") ?? ""
  );
  const initialCondition =
    inputElement.getAttribute("data-condition-source") ??
    inputElement.getAttribute("data-condition") ??
    "";
  const [condition, setCondition] = useState(initialCondition);
  const [query, setQuery] = useState(
    inputElement.getAttribute("data-query") ?? ""
  );
  const [properties, setProperties] = useState<PropertyType[] | null>(null);
  const [joins, setJoins] = useState<JoinConfig[]>([]);
  const [joinedProperties, setJoinedProperties] = useState<
    Map<string, PropertyType[]>
  >(new Map());
  const [testLoading, setTestLoading] = useState(false);
  const [joinCandidateId, setJoinCandidateId] = useState<string>("");
  const [mainTableName, setMainTableName] = useState<string>("");
  const [mainTableLabel, setMainTableLabel] = useState<string>("");

  // State جدید برای چک‌باکس واکشی درون فرم
  const [isFormFetchEnabled, setIsFormFetchEnabled] = useState<boolean>(
    inputElement.getAttribute("data-form-action") === "true"
  );

  const sectionCardClass =
    "rounded-2xl border border-slate-200 bg-white/95 p-4 shadow-sm";

  useEffect(() => {
    conditionElementRef.current = document.getElementById(
      conditionFieldId
    ) as HTMLElement | null;
  }, [conditionFieldId]);

  useEffect(() => {
    if (pendingCursorRef.current == null) return;
    const elementRef = conditionElementRef.current;
    if (!elementRef) return;
    const cursorPosition = pendingCursorRef.current;
    pendingCursorRef.current = null;
    if (typeof window === "undefined") return;
    requestAnimationFrame(() => {
      placeCaretAt(elementRef, cursorPosition ?? 0);
      conditionSelectionRef.current = cursorPosition ?? null;
    });
  }, [condition]);

  useEffect(() => {
    if (inView) fetchNextPage();
  }, [fetchNextPage, inView]);

  useEffect(() => {
    (async () => {
      if (selectedEntityId) {
        const selectedEntity = data?.pages
          .flatMap((page) => page?.data ?? [])
          .find((e) => e.id.toString() === selectedEntityId);
        if (selectedEntity) {
          const realName =
            (selectedEntity as any).tableName ??
            (selectedEntity as any).name ??
            selectedEntity.previewName;
          const label = selectedEntity.previewName ?? realName;
          setMainTableName(realName);
          setMainTableLabel(label);
        }
        const response = await getEntityProperties(selectedEntityId, {
          page: 1,
          size: 100
        });
        setProperties(response?.data ?? null);
      }
    })();
  }, [selectedEntityId]);

  useEffect(() => {
    if (!joinCandidateId && data?.pages?.length) {
      const first = data.pages[0]?.data?.[0];
      if (first) setJoinCandidateId(first.id.toString());
    }
  }, [data, joinCandidateId]);

  // Safety: ensure main table properties remain available even after adding joins
  useEffect(() => {
    (async () => {
      if (selectedEntityId && (!properties || properties.length === 0)) {
        const response = await getEntityProperties(selectedEntityId, {
          page: 1,
          size: 100
        });
        setProperties(response?.data ?? null);
      }
    })();
  }, [joins.length]);

  const getAvailableLeftColumns = (idx: number) => {
    const items: Array<{ key: string; value: string; label: string }> = [];
    if (properties) {
      for (const p of properties) {
        items.push({
          key: `main-${p.id}`,
          value: `${mainTableName}.${p.propertyName}`,
          label: `${mainTableLabel}.${p.previewName}`
        });
      }
    }
    for (let i = 0; i < idx; i += 1) {
      const j = joins[i];
      const props = joinedProperties.get(j.tableId) ?? [];
      for (const p of props) {
        items.push({
          key: `join-${j.tableId}-${p.id}`,
          value: `${j.tableName}.${p.propertyName}`,
          label: `${j.tableLabel}.${p.previewName}`
        });
      }
    }
    return items;
  };

  const captureConditionSelection = useCallback(() => {
    if (typeof window === "undefined") return;
    const elementRef = conditionElementRef.current as HTMLElement | null;
    if (!elementRef) return;

    if (
      elementRef instanceof HTMLTextAreaElement ||
      elementRef instanceof HTMLInputElement
    ) {
      const selStart = (elementRef as HTMLTextAreaElement).selectionStart ?? 0;
      conditionSelectionRef.current = selStart;
      return;
    }

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (!elementRef.contains(range.endContainer)) return;
    const mirroredRange = range.cloneRange();
    mirroredRange.selectNodeContents(elementRef);
    mirroredRange.setEnd(range.endContainer, range.endOffset);
    conditionSelectionRef.current = mirroredRange.toString().length;
  }, []);

  const insertIntoCondition = useCallback(
    (snippet: string, cursorIndex?: number) => {
      if (!snippet) return;
      setCondition((prev) => {
        const currentValue = prev ?? "";
        const cursorPosition =
          conditionSelectionRef.current ?? currentValue.length;
        const safeCursor = Math.min(
          Math.max(cursorPosition, 0),
          currentValue.length
        );
        const nextValue =
          currentValue.slice(0, safeCursor) +
          snippet +
          currentValue.slice(safeCursor);
        const desiredCursorWithinSnippet =
          cursorIndex !== undefined
            ? Math.min(Math.max(cursorIndex, 0), snippet.length)
            : snippet.length;
        pendingCursorRef.current = safeCursor + desiredCursorWithinSnippet;
        return nextValue;
      });
    },
    []
  );

  const handleAddJoin = async (tableId: string) => {
    const selectedEntity = data?.pages
      .flatMap((page) => page?.data ?? [])
      .find((e) => e.id.toString() === tableId);
    if (!selectedEntity) return;
    const joinId = Math.random().toString(36).substring(7);
    const realName =
      (selectedEntity as any).tableName ??
      (selectedEntity as any).name ??
      selectedEntity.previewName;
    const label = selectedEntity.previewName ?? realName;
    const newJoin: JoinConfig = {
      id: joinId,
      tableId,
      tableName: realName,
      tableLabel: label,
      joinKey: "",
      mainTableKey: ""
    };
    setJoins((prev) => [...prev, newJoin]);
    const response = await getEntityProperties(tableId, { page: 1, size: 100 });
    setJoinedProperties((prev) => {
      const next = new Map(prev);
      next.set(tableId, response?.data ?? []);
      return next;
    });
  };

  const handleRemoveJoin = (joinId: string) => {
    setJoins((prev) => prev.filter((j) => j.id !== joinId));
  };

  const handleUpdateJoinKey = (joinId: string, key: string, value: string) => {
    setJoins((prev) =>
      prev.map((j) => (j.id === joinId ? { ...j, [key]: value } : j))
    );
  };

  const handleChangeJoinTable = async (joinId: string, newTableId: string) => {
    const selectedEntity = data?.pages
      .flatMap((page) => page?.data ?? [])
      .find((e) => e.id.toString() === newTableId);
    if (!selectedEntity) return;
    const realName =
      (selectedEntity as any).tableName ??
      (selectedEntity as any).name ??
      selectedEntity.previewName;
    const label = selectedEntity.previewName ?? realName;
    setJoins((prev) =>
      prev.map((j) =>
        j.id === joinId
          ? {
              ...j,
              tableId: newTableId,
              tableName: realName,
              tableLabel: label,
              joinKey: ""
            }
          : j
      )
    );
    const response = await getEntityProperties(newTableId, {
      page: 1,
      size: 100
    });
    setJoinedProperties((prev) => {
      const next = new Map(prev);
      next.set(newTableId, response?.data ?? []);
      return next;
    });
  };

  const buildRelationString = (joinConfigs: JoinConfig[]): string =>
    joinConfigs
      .filter((j) => j.mainTableKey && j.joinKey)
      .map((j) => {
        const lhs = j.mainTableKey.includes(".")
          ? j.mainTableKey
          : mainTableName
            ? `${mainTableName}.${j.mainTableKey}`
            : j.mainTableKey;
        const rhs = j.joinKey.includes(".")
          ? j.joinKey
          : j.tableName
            ? `${j.tableName}.${j.joinKey}`
            : j.joinKey;
        return `JOIN ${j.tableName} ON ${lhs} = ${rhs}`;
      })
      .join(" ");

  const handleAttachAttributes = () => {
    if (!element) return;
    element.setAttribute("data-tableId", selectedEntityId ?? "");
    element.setAttribute("data-filter", filter);
    const tsqlCondition = convertFormulaToTsql(condition);
    element.setAttribute("data-condition", tsqlCondition);
    element.setAttribute("data-condition-source", condition);
    element.setAttribute("data-relation", buildRelationString(joins));
    element.setAttribute("data-query", query);

    // تنظیم attribute چک‌باکس
    if (isFormFetchEnabled) {
      element.setAttribute("data-form-action", "true");
    } else {
      element.setAttribute("data-form-action", "false");
    }

    if (!selectedEntityId) {
      toast.error("لطفا یک جدول رو انتخاب کنید");
      return;
    }
    toast.success("با موفقیت تبدیل شد");
    if (onConfirm) onConfirm();
    onClose();
  };

  const handleTestQuery = async () => {
    if (!selectedEntityId) {
      toast.error("لطفا یک جدول رو انتخاب کنید");
      return;
    }
    setTestLoading(true);
    try {
      toast.success("کوئری اجرا شد - بدون خطا");
    } catch {
      toast.error("خطا در اجرای کوئری");
    } finally {
      setTestLoading(false);
    }
  };

  if (isLoading)
    return (
      <div className="flex justify-center py-10">
        <HashLoader color="#0099A5" />
      </div>
    );

  return (
    <div className="max-h-[72vh] w-full space-y-5 overflow-y-auto pr-1">
      <div className={sectionCardClass}>
        <label className="mb-2 block text-sm font-semibold text-slate-800">
          جدول اصلی
        </label>
        <Select
          dir="rtl"
          open={isOpen}
          onOpenChange={setIsOpen}
          onValueChange={(value) => {
            setSelectedEntityId(value);
            setIsOpen(false); // فقط موقع انتخاب آیتم بسته بشه
          }}
          defaultValue={selectedEntityId ?? ""}
        >
          <SelectTrigger>
            <SelectValue placeholder="جدول را انتخاب کنید" />
          </SelectTrigger>
          <SelectContent>
            <div className="sticky top-0 z-10 px-2 py-1.5">
              <input
                type="text"
                placeholder="جستجو..."
                dir="rtl"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-400"
              />
            </div>

            {isLoading ? (
              <div className="flex justify-center py-4">
                <HashLoader size={24} color="#0099A5" />
              </div>
            ) : (
              <>
                {data?.pages.map((entities, index) => (
                  <Fragment key={index + 1}>
                    {entities?.data.map((entity) => (
                      <SelectItem key={entity.id} value={entity.id.toString()}>
                        {entity.previewName}
                      </SelectItem>
                    ))}
                  </Fragment>
                ))}
                {hasNextPage && (
                  <div
                    ref={ref}
                    className="flex w-full justify-center py-1 text-center"
                  >
                    {isFetchingNextPage ? (
                      <HashLoader size={30} color="#0099A5" />
                    ) : (
                      "بیشتر"
                    )}
                  </div>
                )}
              </>
            )}
          </SelectContent>
        </Select>
      </div>

      {/* ستون‌ها فقط در بخش نمایش مقدار نشان داده می‌شوند */}

      <div className={sectionCardClass}>
        <div className="mb-3 flex items-center justify-between">
          <label className="block text-sm font-semibold text-slate-800">
            JOINها
          </label>
          <div className="flex items-center gap-2">
            <Select value={joinCandidateId} onValueChange={setJoinCandidateId}>
              <SelectTrigger className="h-8 w-48 text-xs">
                <SelectValue placeholder="انتخاب جدول JOIN" />
              </SelectTrigger>
              <SelectContent>
                {data?.pages.map((entities, index) => (
                  <Fragment key={index + 1}>
                    {entities?.data.map((entity) => (
                      <SelectItem key={entity.id} value={entity.id.toString()}>
                        {entity.previewName}
                      </SelectItem>
                    ))}
                  </Fragment>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant="outline"
              className="h-8 px-3"
              onClick={() => joinCandidateId && handleAddJoin(joinCandidateId)}
            >
              اضافه کن
            </Button>
          </div>
        </div>
        {joins.length === 0 ? (
          <p className="py-4 text-center text-sm text-slate-500">
            هنوز هیچ JOIN اضافه نشده است
          </p>
        ) : (
          <div className="space-y-3">
            {joins.map((join, idx) => (
              <div
                key={join.id}
                className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700">
                    JOIN {idx + 1}: {join.tableLabel}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2"
                    onClick={() => handleRemoveJoin(join.id)}
                  >
                    حذف
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-slate-600">جدول JOIN</label>
                    <Select
                      value={join.tableId}
                      onValueChange={(val) =>
                        handleChangeJoinTable(join.id, val)
                      }
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="انتخاب کنید" />
                      </SelectTrigger>
                      <SelectContent>
                        {data?.pages.map((entities, index) => (
                          <Fragment key={index + 1}>
                            {entities?.data.map((entity) => (
                              <SelectItem
                                key={entity.id}
                                value={entity.id.toString()}
                              >
                                {entity.previewName}
                              </SelectItem>
                            ))}
                          </Fragment>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-600">
                      کلید (از جداول موجود)
                    </label>
                    <Select
                      value={join.mainTableKey}
                      onValueChange={(val) =>
                        handleUpdateJoinKey(join.id, "mainTableKey", val)
                      }
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="انتخاب کنید" />
                      </SelectTrigger>
                      <SelectContent>
                        {getAvailableLeftColumns(idx).map((opt) => (
                          <SelectItem key={opt.key} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-600">
                      کلید جدول JOIN
                    </label>
                    <Select
                      value={join.joinKey}
                      onValueChange={(val) =>
                        handleUpdateJoinKey(join.id, "joinKey", val)
                      }
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="انتخاب کنید" />
                      </SelectTrigger>
                      <SelectContent>
                        {joinedProperties.get(join.tableId)?.map((prop) => (
                          <SelectItem
                            key={prop.id}
                            value={`${join.tableName}.${prop.propertyName}`}
                          >{`${join.tableLabel}.${prop.previewName}`}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {/* فیلدهای جدول JOIN در بخش نمایش مقدار ارائه می‌شوند */}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* چک‌باکس واکشی درون فرم - اضافه شده */}
      <div className={sectionCardClass}>
        <div className="flex items-center space-x-2 space-x-reverse">
          <Checkbox
            id="form-fetch-checkbox"
            checked={isFormFetchEnabled}
            onCheckedChange={(checked) =>
              setIsFormFetchEnabled(checked === true)
            }
          />
          <label
            htmlFor="form-fetch-checkbox"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            واکشی درون فرم
          </label>
        </div>
      </div>

      <div className={sectionCardClass}>
        <label className="mb-2 block text-sm font-semibold text-slate-800">
          شرط فیلتر
        </label>
        <HighlightInput
          dir="ltr"
          value={filter}
          onChange={setFilter}
          className="h-12 w-full border border-slate-300 bg-white"
          style={{
            display: "block",
            width: "100%",
            minHeight: 40,
            padding: "5px 7px",
            border: "1px solid rgb(204,204,204)",
            borderRadius: 6
          }}
          highlight={[
            {
              pattern: /{{\s*([^}]*)\s*}}/g,
              style: "background-color:#0099A520; color:#0099A5"
            },
            {
              pattern: /\[\[\s*([^\]]+)\s*\]\]/g,
              style: "background-color:#0099A540; color:#0099A5"
            }
          ]}
        />
        {/* دکمه‌های گزینه یک و گزینه دو - فقط در صورت فعال بودن چک‌باکس نمایش داده می‌شوند */}
        {isFormFetchEnabled && (
          <div className="mt-2">
            <div className="flex flex-wrap gap-3">
              {dataNames.map(({ dataName, id }, index) => (
                <Button
                  key={index}
                  variant="default"
                  className="min-w-[100px] flex-1 bg-primary hover:bg-blue-700"
                  onClick={() => {
                    const snippet = `{{Element:${id}}}`;
                    setFilter((prev) => `${prev}${snippet}`);
                  }}
                >
                  {dataName}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className={`${sectionCardClass} space-y-4`}>
        <div className="mb-2 flex items-center justify-between">
          <label className="block text-sm font-semibold text-slate-800">
            نمایش مقدار
          </label>
          <span className="text-xs text-slate-500">
            از نمادهای بالا یا فیلدهای زیر استفاده کنید
          </span>
        </div>
        <FormulaToolbar
          onInsert={(snippet, cursorIndex) =>
            insertIntoCondition(snippet, cursorIndex)
          }
        />
        <textarea
          id={conditionFieldId}
          dir="ltr"
          value={condition}
          onChange={(e) => setCondition(e.target.value)}
          onClick={captureConditionSelection}
          onKeyUp={captureConditionSelection}
          onMouseUp={captureConditionSelection}
          onFocus={captureConditionSelection}
          className="h-12 w-full border border-slate-300 bg-white"
          style={{
            display: "block",
            width: "100%",
            minHeight: 40,
            padding: "5px 7px",
            border: "1px solid rgb(204,204,204)",
            borderRadius: 6
          }}
        />
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {properties?.map((property) => (
            <Button
              key={`main-${property.id}`}
              size="sm"
              variant="secondary"
              className="h-fit px-3 py-2 text-xs"
              onClick={() => {
                const cursorIndex =
                  conditionSelectionRef.current ?? condition.length;
                const needsLeadingSpace =
                  cursorIndex > 0 &&
                  !/\s$/.test(condition.slice(0, cursorIndex));
                const snippet = `${needsLeadingSpace ? " " : ""}{{${mainTableName}.${property.propertyName}}}`;
                insertIntoCondition(snippet);
              }}
            >
              {`${mainTableLabel}.${property.previewName}`}
            </Button>
          ))}
          {Array.from(joinedProperties.entries()).map(([tblId, props]) =>
            props.map((prop) => (
              <Button
                key={`${tblId}-${prop.id}`}
                size="sm"
                variant="secondary"
                className="h-fit px-3 py-2 text-xs"
                onClick={() => {
                  const cursorIndex =
                    conditionSelectionRef.current ?? condition.length;
                  const needsLeadingSpace =
                    cursorIndex > 0 &&
                    !/\s$/.test(condition.slice(0, cursorIndex));
                  const joinInfo = joins.find((j) => j.tableId === tblId);
                  const tablePrefix = joinInfo?.tableName ?? "";
                  const snippet = `${needsLeadingSpace ? " " : ""}{{${tablePrefix}.${prop.propertyName}}}`;
                  insertIntoCondition(snippet);
                }}
              >
                {(() => {
                  const joinInfo = joins.find((j) => j.tableId === tblId);
                  const labelPrefix =
                    joinInfo?.tableLabel ?? joinInfo?.tableName ?? "";
                  return `${labelPrefix}.${prop.previewName}`;
                })()}
              </Button>
            ))
          )}
        </div>
      </div>

      <div className={sectionCardClass}>
        <label className="mb-2 block text-sm font-semibold text-slate-800">
          کوئری دیتابیس
        </label>
        <Input
          dir="ltr"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="h-12 w-full border border-slate-300 bg-white"
        />
      </div>

      <div className="flex gap-2">
        <Button
          variant="outline"
          className="flex-1"
          onClick={handleTestQuery}
          disabled={testLoading}
        >
          {testLoading ? <HashLoader size={16} color="#0099A5" /> : "تست کوئری"}
        </Button>
        <Button className="flex-1" onClick={handleAttachAttributes}>
          تبدیل
        </Button>
      </div>
    </div>
  );
};
type OperandSegment = {
  value: string;
  start: number;
  end: number;
};

const convertFormulaToTsql = (formula: string) => {
  if (!formula) return "";
  let result = formula;

  result = result.replace(/به\s+توان/gi, "^");
  result = result.replace(/÷/g, "/").replace(/×/g, "*");
  result = result.replace(/٪/g, "%");

  result = result.replace(/\[\[\s*([\s\S]*?)\s*\]\]/g, (_, inner: string) => {
    let s = inner;
    s = s.replace(/به\s+توان/gi, "^");
    s = s.replace(/÷/g, "/").replace(/×/g, "*");
    s = s.replace(/٪/g, "%");
    s = stripDoubleCurly(s);
    s = s.replace(/√\s*\(([^)]*)\)/g, (_, expr) => `SQRT(${expr.trim()})`);
    s = s.replace(/√\s*([A-Za-z0-9_.]+)/g, (_, id) => `SQRT(${id.trim()})`);
    s = s.replace(/^\s*√\s*$/g, "SQRT");
    s = s.replace(/∑\s*\(([^)]*)\)/g, (_, expr) => `SUM(${expr.trim()})`);
    s = s.replace(/^\s*∑\s*$/g, "SUM");
    s = s.replace(/\|\s*([^|]*?)\s*\|/g, (_, expr) =>
      expr.trim() ? `ABS(${expr.trim()})` : `ABS()`
    );
    s = convertCaretPowerOperators(s);
    return normalizeWhitespace(s);
  });

  // result = stripDoubleCurly(result);
  result = convertCaretPowerOperators(result);
  return normalizeWhitespace(result);
};

const stripDoubleCurly = (value: string) =>
  value.replace(/{{\s*([^}]*)\s*}}/g, "$1");

//

const convertCaretPowerOperators = (value: string) => {
  let result = value;
  let caretIndex = result.indexOf("^");

  while (caretIndex !== -1) {
    const leftOperand = extractLeftOperand(result, caretIndex - 1);
    const rightOperand = extractRightOperand(result, caretIndex + 1);

    if (!leftOperand || !rightOperand) {
      caretIndex = result.indexOf("^", caretIndex + 1);
      continue;
    }

    const replacement = `POWER(${leftOperand.value}, ${rightOperand.value})`;
    result =
      result.slice(0, leftOperand.start) +
      replacement +
      result.slice(rightOperand.end);
    caretIndex = result.indexOf("^", leftOperand.start + replacement.length);
  }

  return result;
};

const extractLeftOperand = (
  text: string,
  index: number
): OperandSegment | null => {
  let cursor = index;
  while (cursor >= 0 && /\s/.test(text[cursor])) {
    cursor -= 1;
  }
  if (cursor < 0) return null;

  // Handle {{tableName.field}} template tokens
  if (text[cursor] === "}" && cursor > 0 && text[cursor - 1] === "}") {
    const openIdx = text.lastIndexOf("{{", cursor - 2);
    if (openIdx !== -1) {
      return {
        value: text.slice(openIdx, cursor + 1),
        start: openIdx,
        end: cursor + 1
      };
    }
  }

  if (text[cursor] === ")") {
    const start = findMatchingLeftParen(text, cursor);
    if (start === -1) return null;
    let operandStart = start;
    while (operandStart - 1 >= 0 && isIdentifierChar(text[operandStart - 1])) {
      operandStart -= 1;
    }
    return {
      value: text.slice(operandStart, cursor + 1).trim(),
      start: operandStart,
      end: cursor + 1
    };
  }

  if (text[cursor] === "'" || text[cursor] === '"') {
    const quoteStart = findMatchingQuoteLeft(text, cursor, text[cursor]);
    if (quoteStart === -1) return null;
    return {
      value: text.slice(quoteStart, cursor + 1),
      start: quoteStart,
      end: cursor + 1
    };
  }

  let start = cursor;
  while (start >= 0 && isIdentifierChar(text[start])) {
    start -= 1;
  }
  start += 1;
  if (start > cursor) return null;

  return {
    value: text.slice(start, cursor + 1).trim(),
    start,
    end: cursor + 1
  };
};

const extractRightOperand = (
  text: string,
  index: number
): OperandSegment | null => {
  let cursor = index;
  while (cursor < text.length && /\s/.test(text[cursor])) {
    cursor += 1;
  }
  if (cursor >= text.length) return null;

  if (text[cursor] === "'" || text[cursor] === '"') {
    const quoteEnd = findMatchingQuoteRight(text, cursor, text[cursor]);
    if (quoteEnd === -1) return null;
    return {
      value: text.slice(cursor, quoteEnd + 1),
      start: cursor,
      end: quoteEnd + 1
    };
  }

  if (text[cursor] === "(") {
    const parenEnd = findMatchingRightParen(text, cursor);
    if (parenEnd === -1) return null;
    return {
      value: text.slice(cursor, parenEnd + 1).trim(),
      start: cursor,
      end: parenEnd + 1
    };
  }

  let end = cursor;
  while (end < text.length && isIdentifierChar(text[end])) {
    end += 1;
  }

  if (text[end] === "(") {
    const parenEnd = findMatchingRightParen(text, end);
    if (parenEnd !== -1) {
      end = parenEnd + 1;
    }
  }

  if (end === cursor) return null;

  return {
    value: text.slice(cursor, end).trim(),
    start: cursor,
    end
  };
};

const findMatchingLeftParen = (text: string, index: number) => {
  let depth = 0;
  for (let i = index; i >= 0; i -= 1) {
    if (text[i] === ")") {
      depth += 1;
    } else if (text[i] === "(") {
      depth -= 1;
      if (depth === 0) {
        return i;
      }
    }
  }
  return -1;
};

const findMatchingRightParen = (text: string, index: number) => {
  let depth = 0;
  for (let i = index; i < text.length; i += 1) {
    if (text[i] === "(") {
      depth += 1;
    } else if (text[i] === ")") {
      depth -= 1;
      if (depth === 0) {
        return i;
      }
    }
  }
  return -1;
};

const findMatchingQuoteLeft = (text: string, index: number, quote: string) => {
  for (let i = index - 1; i >= 0; i -= 1) {
    if (text[i] === quote && (i === 0 || text[i - 1] !== "\\")) {
      return i;
    }
  }
  return -1;
};

const findMatchingQuoteRight = (text: string, index: number, quote: string) => {
  for (let i = index + 1; i < text.length; i += 1) {
    if (text[i] === quote && (i === 0 || text[i - 1] !== "\\")) {
      return i;
    }
  }
  return -1;
};

const isIdentifierChar = (char: string) => /[A-Za-z0-9_.$]/.test(char);

const normalizeWhitespace = (value: string) =>
  value.replace(/\s+/g, " ").trim();

const placeCaretAt = (element: HTMLElement, position: number) => {
  if (typeof window === "undefined") return;

  if (
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLInputElement
  ) {
    const len = (element as HTMLTextAreaElement).value?.length ?? 0;
    const pos = Math.max(0, Math.min(position, len));
    (element as HTMLTextAreaElement).setSelectionRange(pos, pos);
    (element as HTMLTextAreaElement).focus();
    return;
  }

  const selection = window.getSelection();
  if (!selection) return;
  const range = document.createRange();
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);
  let remaining = Math.max(0, position);
  let currentNode = walker.nextNode();

  while (currentNode) {
    const length = currentNode.textContent?.length ?? 0;
    if (remaining <= length) {
      range.setStart(currentNode, remaining);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      return;
    }
    remaining -= length;
    currentNode = walker.nextNode();
  }

  range.selectNodeContents(element);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
};

export default ReceiveDatabaseModal;
